import { AgentExcelFileService } from '../../services/agent-excel-file.service';
import { WsToolDeclaration } from '../ws/agent-ws.types';
import { AgentTool, AgentToolResult } from './agent-tool.types';

/**
 * Инструмент клиентской сборки Excel-файла.
 * Сам файл собирается и хранится на клиенте, агенту возвращается только fileId для блока `excel_file`.
 */
export class AgentExcelExportTool implements AgentTool {
    /** @inheritdoc */
    public readonly declaration: WsToolDeclaration = {
        toolName: 'create_excel_file',
        description:
            'Создаёт Excel-файл (.xlsx) на стороне пользователя и возвращает fileId для блока excel_file. '
            + 'Вызывай, когда пользователь просит выгрузить, сохранить или скачать данные в Excel, сформировать таблицу/отчёт файлом, либо когда табличный ответ удобнее отдать файлом. '
            + 'rows — обязательный массив строк; каждая строка — массив значений строго одного из типов: string, number, boolean, null. Объекты, вложенные массивы и форматирование ячеек не поддерживаются. '
            + 'columns — необязательная строка заголовков (массив строк), выводится первой строкой жирным. '
            + 'columnWidths — необязательные ширины колонок (массив положительных чисел). '
            + 'columnTypes — необязательные типы колонок: auto (по умолчанию), number, date, text. Для дат обязательно указывай date, а значения передавай строками вида "2026-10-02" или "02.10.2026". '
            + 'columnFormats — необязательные Excel-форматы по колонкам (например "#,##0.00", "#,##0.00 ₽", "0.00%", "DD.MM.YYYY"); null — без формата. Формат для текстовой колонки не поддерживается. '
            + 'Данные передавай прямо в аргументах; не дублируй их в тексте ответа и не пиши ссылку на скачивание в сообщении. '
            + 'Если инструмент вернул ошибку — исправь аргументы по тексту ошибки и вызови его снова, не сообщая пользователю о неудачной попытке. '
            + 'После успеха собери блок excel_file с fileId из ответа. Один вызов — один блок.',
        requestFormat: {
            fileName: 'string - имя файла без расширения (необязательно)',
            sheetName: 'string - имя листа (необязательно)',
            columns: 'array of string - заголовки колонок (необязательно)',
            rows: 'array of arrays - данные; значения только string | number | boolean | null',
            columnWidths: 'array of number - ширины колонок (необязательно)',
            columnTypes: "array of ('auto'|'number'|'date'|'text') - типы колонок (необязательно)",
            columnFormats: 'array of string|null - Excel-форматы колонок (необязательно)',
        },
        responseFormat: {
            fileId: 'string - id файла, передай его в блок excel_file',
            fileName: 'string',
            sheetName: 'string',
            rowCount: 'number',
            columnCount: 'number',
            size: 'number - размер файла в байтах',
        },
    };

    private readonly excelFileService: AgentExcelFileService;

    public constructor(excelFileService: AgentExcelFileService) {
        this.excelFileService = excelFileService;
    }

    /** @inheritdoc */
    public async execute(args: Record<string, unknown>): Promise<AgentToolResult> {
        const result = await this.excelFileService.createFile(args ?? {});

        if (result.status === 'error') {
            return {
                ok: false,
                error: { code: result.code, message: result.message },
            };
        }

        return {
            ok: true,
            payload: {
                ...result.info,
                hint: 'Собери блок excel_file с полем fileId из этого ответа.',
            },
        };
    }
}
